import { Box } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  ICompanyType,
  ICountry,
  IInterest,
  IMembershipType,
  IRecipientListInput,
  ITitleInternal,
  useCompanyTypesQuery,
  useCountriesQuery,
  useCreateRecipientListMutation,
  useGetInterestsQuery,
  useMembershipTypesQuery,
  useTitlesInternalQuery,
  useUpdateRecipientListMutation
} from '../../../generated/graphql';
import { RecipientListType } from '../../../models/recipientList';
import { sameRecipientListExists } from '../../../utils/clientUtils';
import { objectsAreEquel } from '../../../utils/mathUtils';
import { canUserEdit } from '../../../utils/rightUtils';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import DialogBox from '../../Common/Layout/DialogBox';
import FormStepper from '../../Common/Layout/FormStepper';
import Loader from '../../Common/Loader';
import CompanyTypeStep from './CompanyTypeStep';
import ContactTitleStep from './ContactTitleStep';
import CountryStep from './CountryStep';
import GeneralDetailsTab from './GeneralDetailsTab';
import RecipientsStep from './RecipientsStep';

export type IErrors = {
  name: string | null;
  interest: string | null;
  membership: string | null;
};

const defaultErrorsObj: IErrors = {
  name: null,
  interest: null,
  membership: null
};

interface IProps {
  onClose: () => void;
}

export const ErrorTexts = {
  name: 'Recipient List name is required and should be more than 2 symbols.',
  nameExists: 'This Recipient List name already exists',
  interest: '*Please select at least one interest',
  membership: '*Please select at least one membership'
};

const steps = ['General details', 'Contact Title', 'Company Type', 'Country', 'Recipients'];

const RecipientListStepper: React.FunctionComponent<IProps> = ({ onClose }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [initialRecipientlist] = useState(state.selectedRecipientList);
  const [activeStep, setActiveStep] = useState(0);
  const titlesInternalQuery = useTitlesInternalQuery({ fetchPolicy: 'no-cache' });
  const [titlesInternal, setTitlesInternal] = useState<ITitleInternal[]>([]);
  const membershipTypesQuery = useMembershipTypesQuery({ fetchPolicy: 'no-cache' });
  const [membershipTypes, setMembershipTypes] = useState<IMembershipType[]>([]);
  const interestsQuery = useGetInterestsQuery({ fetchPolicy: 'no-cache' });
  const [interests, setInterests] = useState<IInterest[]>([]);
  const companyTypesQuery = useCompanyTypesQuery({ fetchPolicy: 'no-cache' });
  const [companyTypes, setCompanyTypes] = useState<ICompanyType[]>([]);
  const countriesQuery = useCountriesQuery({ fetchPolicy: 'no-cache' });
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const canEdit = canUserEdit(state.user, Types.RecipientList);
  const [createRecipientList, createRecipientListResult] = useCreateRecipientListMutation();
  const [updateRecipientList, updateRecipientListResult] = useUpdateRecipientListMutation();

  useEffect(() => {
    if (titlesInternalQuery.data && titlesInternalQuery.data.result) {
      setTitlesInternal(titlesInternalQuery.data.result as ITitleInternal[]);
    }
    if (companyTypesQuery.data && companyTypesQuery.data.result) {
      setCompanyTypes(companyTypesQuery.data.result as ICompanyType[]);
    }
    if (membershipTypesQuery.data && membershipTypesQuery.data.result) {
      setMembershipTypes(membershipTypesQuery.data.result as IMembershipType[]);
    }
    if (interestsQuery.data && interestsQuery.data.result) {
      setInterests(interestsQuery.data.result as IInterest[]);
    }
    if (countriesQuery.data && countriesQuery.data.result) {
      setCountries(countriesQuery.data.result as ICountry[]);
    }
  }, [
    titlesInternalQuery.data,
    companyTypesQuery.data,
    membershipTypesQuery.data,
    interestsQuery.data,
    countriesQuery.data
  ]);

  const getStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <GeneralDetailsTab
            membershipTypes={membershipTypes}
            interests={interests}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
        );
      case 1:
        return <ContactTitleStep data={titlesInternal} />;
      case 2:
        return <CompanyTypeStep data={companyTypes} />;
      case 3:
        return <CountryStep countries={countries} />;
      case 4:
        return <RecipientsStep />;
      default:
        return 'Unknown stepIndex';
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      let hasErrors =
        formErrors.interest || formErrors.name || formErrors.membership ? true : false;
      let currentErrors = formErrors;
      if (formErrors.name === null) {
        if (state.selectedRecipientList?.name && state.selectedRecipientList.name.length > 1) {
          if (
            sameRecipientListExists(
              state.selectedRecipientList.name.trim(),
              parseInt(state.selectedRecipientList?.id ?? '')
            )
          ) {
            hasErrors = true;
            currentErrors = { ...currentErrors, name: ErrorTexts.nameExists };
          }
        } else {
          hasErrors = true;
          currentErrors = { ...currentErrors, name: ErrorTexts.name };
        }
      }
      if (formErrors.interest === null) {
        if (state.selectedRecipientList?.interestIds.length === 0) {
          hasErrors = true;
          currentErrors = { ...currentErrors, interest: ErrorTexts.interest };
        }
      }
      if (formErrors.membership === null) {
        if (state.selectedRecipientList?.membershipTypeIds.length === 0) {
          hasErrors = true;
          currentErrors = { ...currentErrors, membership: ErrorTexts.membership };
        }
      }
      if (hasErrors) {
        setFormErrors(currentErrors);
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else if (activeStep === 4) {
      const selectedRecipientList = state.selectedRecipientList as RecipientListType;
      // Check if recipient list is new or old!
      if (selectedRecipientList.id) {
        if (isValidRecipientList(selectedRecipientList)) {
          if (objectsAreEquel(initialRecipientlist, state.selectedRecipientList)) {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully updated recipient list ${selectedRecipientList.name}`
              }
            });
            onClose();
          } else {
            updateRecipientList({
              variables: {
                input: getRecipientListDto(selectedRecipientList, false)
              },
              update(_, { data }) {
                if (data?.result) {
                  dispatch({
                    type: Actions.ShowMessage,
                    payload: {
                      severity: 'success',
                      text: `You have successfully updated recipient list ${selectedRecipientList.name}`
                    }
                  });
                  onClose();
                }
              }
            }).catch((e) => {
              dispatch({
                type: Actions.ShowMessage,
                payload: {
                  severity: 'error',
                  text: e.message,
                  autoHide: 6000
                }
              });
            });
          }
        }
      } else {
        if (isValidRecipientList(selectedRecipientList)) {
          createRecipientList({
            variables: {
              input: getRecipientListDto(selectedRecipientList, true)
            },
            update(_, { data }) {
              if (data?.result) {
                dispatch({
                  type: Actions.ShowMessage,
                  payload: {
                    severity: 'success',
                    text: `You have successfully created new recipient list ${selectedRecipientList.name}`
                  }
                });
                onClose();
              }
            }
          }).catch((e) => {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'error',
                text: e.message,
                autoHide: 6000
              }
            });
          });
        }
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const isValidRecipientList = (selectedRecipientList: RecipientListType) => {
    if (!selectedRecipientList.name || selectedRecipientList.name.length < 2) {
      return false;
    }
    if (!selectedRecipientList.membershipTypeIds.length) {
      return false;
    }
    if (!selectedRecipientList.interestIds.length) {
      return false;
    }
    return true;
  };

  const getRecipientListDto = (
    selectedRecipientList: RecipientListType,
    isNew: boolean
  ): IRecipientListInput => {
    const result: IRecipientListInput = {
      name: selectedRecipientList.name as string,
      membership_type_ids: selectedRecipientList.membershipTypeIds.map((m) => parseInt(m)),
      interest_ids: selectedRecipientList.interestIds.map((i) => parseInt(i)),
      company_type_ids: selectedRecipientList.companyTypeIds.map((c) => parseInt(c)),
      contact_title_ids: selectedRecipientList.contactTitleIds.map((c) => parseInt(c)),
      country_ids: selectedRecipientList.countryIds.map((c) => parseInt(c)),
      excluded_contact_ids: selectedRecipientList.excludedContactIds,
      included_contact_ids: selectedRecipientList.includedContactIds
    };
    if (!isNew) {
      result.id = parseInt(selectedRecipientList.id as string);
    }

    return result;
  };

  return (
    <DialogBox
      id="recipient-list-wizard-stepper"
      title={state.selectedRecipientList?.id ? 'List Configuration' : 'Create List'}
      width={1200}
      height={843}
      onClose={onClose}
    >
      <FormStepper steps={steps} activeStep={activeStep} />
      <Box sx={{ minHeight: '370px' }}>{getStepContent(activeStep)}</Box>
      <Box className="centralize" sx={{ gridColumn: 'span 3', mt: '20px' }}>
        {activeStep !== 0 && (
          <CancelButton id="recipient-list-wizard-back-button" onClick={handleBack} text="Back" />
        )}
        <SaveButton
          id={`recipient-list-wizard-${activeStep === steps.length - 1 ? 'save' : 'next'}-button`}
          disabled={activeStep === steps.length - 1 && !canEdit}
          onClick={handleNext}
          text={activeStep === steps.length - 1 ? 'Save' : 'Next'}
        />
      </Box>
      <Loader
        loading={
          titlesInternalQuery.loading ||
          companyTypesQuery.loading ||
          countriesQuery.loading ||
          membershipTypesQuery.loading ||
          createRecipientListResult.loading ||
          updateRecipientListResult.loading
        }
      />
    </DialogBox>
  );
};

export default RecipientListStepper;
